import { AnswerDownloadDto, LanguageStringDownloadDto, MediaDownloadDto, MediaUploadDto } from 'collaboration-service';
import { MediaType } from 'components/MediaSelection/FileSelector';
import MediaSelection from 'components/MediaSelection/MediaSelection';
import { Button, Icon, useThemePart } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { useImgI18N } from 'services/ImgI18N';
import { BaseEditItem } from '../GeneralPostEdit';
import { useGPEState } from '../Helpers/UseGPEState';
import { PostEditElementProps } from '../TypeImplementations/PostEdit';
import { EditLanguageStringsBase } from './EditLanguageStrings';
import CancelWithConfirmation from 'components/CancelWithConfirmation';
import SwitchButton from 'components/Switch/SwitchButton';

interface EditAnswersProps<T> extends PostEditElementProps<T, AnswerDownloadDto[]> {
    noImage?: boolean;
    noIsCorrect?: boolean;
    noInput?: boolean;
    inPairs?: boolean;
}

const EditAnswers = <T extends BaseEditItem = BaseEditItem>(p: EditAnswersProps<T>) => {
    const { getter, noImage, noIsCorrect, noInput, inPairs } = p;
    const { value, setValue, addFileTo } = useGPEState(getter);
    const { currentLanguage, t } = useImgI18N("admin");
    const contentWidth = useThemePart(x => x.content.width);

    const onSelectCorrect = React.useMemo(() => (i: number) => (checked: boolean) => {
        const copy = _.cloneDeep(value);
        copy[i].isCorrect = !checked;
        setValue(copy);
    }, [value, setValue]);

    const onChangeDescriptionTable = React.useMemo(() => (i: number) => (val: LanguageStringDownloadDto[] | ((val: LanguageStringDownloadDto[]) => LanguageStringDownloadDto[])) => {
        if (Array.isArray(val)) {
            const copy = _.cloneDeep(value);
            copy[i].descriptions = val;
            setValue(copy);
        }
    }, [value, setValue]);

    const onMediaSelected = React.useMemo(() => (i: number) => (file: File, media: MediaUploadDto) => {
        const answers = _.clone(value);
        (media as any).file = file ? file : null;
        addFileTo([i.toString(), 'media']);
        answers[i].media = media as MediaDownloadDto;
        setValue(answers);
    }, [value, addFileTo, setValue]);

    const add = React.useMemo(() => () => {
        const elem: AnswerDownloadDto = {
            isCorrect: false,
            descriptions: [{ lng: currentLanguage, text: 'generated' }],
            links: [],
            id: ''
        };
        let copy = _.cloneDeep(value);
        copy.push(elem);
        if (inPairs) {
            copy.push(elem);
        }
        setValue(copy);

    }, [value, setValue, currentLanguage, inPairs]);

    const onClickDelete = React.useMemo(() => (i: number, even: boolean) => () => {
        let copy = _.cloneDeep(value);
        if (!inPairs)
            copy.splice(i, 1);
        else
            copy.splice(even ? (i - 1) : i, 2);
        setValue(copy);
    }, [value, setValue, inPairs]);

    let pairNumber = 0;
    return (
        <div style={{ border: "1px solid #b5b4ba" }}>
            <div style={{ marginBottom: 0, height: 40, background: "#f5f4f7", borderBottom: "1px solid #b5b4ba" }}>
                <Button
                    kind='sendButton'
                    icon='add'
                    floated='right'
                    onClick={add}
                    content={inPairs ? t('add pair of elements') : t('add element')}
                />
            </div>
            <div style={{ padding: 10, width: "100%" }}>

                {_.map(value, (an, i) => {
                    const even = (i % 2 !== 0);
                    pairNumber += even ? 1 : 0;
                    return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '271px 1fr 40px', columnGap: 5, marginBottom: 24, rowGap: 10, width: "calc(100% - 1px)", alignItems: "start" }}>
                            <div style={{ boxShadow: "0 6px 8px -6px #000" }}>
                                {inPairs && even &&
                                    <MediaSelection
                                        media={an?.media}
                                        onMediaSelected={onMediaSelected(i)}
                                        uploadType={MediaType.IMAGE}
                                        border={`2px solid ${an.isCorrect ? "#018A16" : "#E00000"}`}
                                        showPlaceholderImage
                                    />
                                }
                                {inPairs === undefined && !noImage &&
                                    <MediaSelection
                                        media={an?.media}
                                        onMediaSelected={onMediaSelected(i)}
                                        uploadType={MediaType.IMAGE}
                                        border={`2px solid ${an.isCorrect ? "#018A16" : "#E00000"}`}
                                        showPlaceholderImage
                                    />
                                }
                            </div>
                            <div style={{ padding: 10, display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 10, alignItems: "center" }}>
                                {!noIsCorrect &&
                                    <>
                                        {/* <Checkbox selected={an.isCorrect} label={'isCorrect'} onClick={onSelectCorrect(i)} /> */}
                                        <SwitchButton
                                            isActive={onSelectCorrect(i)}
                                            initialState={an.isCorrect}
                                            floated='left'
                                        />
                                        <div>{t("this answer is correct?")}</div>
                                        <div><Icon name={an.isCorrect ? "thumb up" : "thumb down"} color={an.isCorrect ? "@accentGreen" : "@accentRed"} size={24} style={{ float: "right", paddingTop: an.isCorrect ? 8 : 12 }} marginTop={0} /></div>
                                        <div style={{ paddingTop: 20, fontSize: "1.1em", fontWeight: "bolder", color: an.isCorrect ? "#018A16" : "#E00000" }}>{an.isCorrect ? t("yes, this answer is correct") : t("no, this answer is incorrect")}.</div>
                                    </>
                                }
                            </div>
                            <div>
                                {/* <Button
                                    kind={!inPairs ? 'primary' : (!even ? 'primary' : 'halfTransparentButton')}
                                    icon='delete'
                                    onClick={onClickDelete(i, even)}
                                    tooltip={{ tooltipText: inPairs ? (t('delete this pair of elements ') + (!even ? pairNumber : pairNumber - 1)) : t('delete element') }}
                                /> */}
                                <CancelWithConfirmation
                                    cancel={onClickDelete(i, even)}
                                    floated='right'
                                    cancelText='cancel'
                                    iconOnly
                                    confirmText={inPairs ? (t('yes, delete this pair of elements ') + (!even ? pairNumber : pairNumber - 1)) : t('yes, delete element')}
                                    openAbsolute
                                    icon='delete'
                                    kind='cancelButton'
                                    tooltipInitialButton={inPairs ? (t('delete this pair of elements ') + (!even ? pairNumber : pairNumber - 1)) : t('delete element')}
                                />
                            </div>
                            {inPairs === undefined && !noInput &&
                                <div style={{ gridColumn: '1 / span 3' }}>
                                    <EditLanguageStringsBase<AnswerDownloadDto> value={an.descriptions} setValue={onChangeDescriptionTable(i)} fixWidth={contentWidth - 293} />
                                </div>
                            }
                            {inPairs && !even &&
                                <div style={{ gridColumn: '1 / span 3' }}>
                                    <EditLanguageStringsBase<AnswerDownloadDto> value={an.descriptions} setValue={onChangeDescriptionTable(i)} fixWidth={contentWidth - 293} />
                                </div>
                            }
                        </div>
                    );
                })}
            </div>
            <div style={{ marginBottom: 0, height: 40, background: "#f5f4f7", borderTop: "1px solid #b5b4ba" }}>
                <Button
                    kind='sendButton'
                    icon='add'
                    floated='right'
                    onClick={add}
                    // tooltip={{ tooltipText: inPairs ? t('add pair of elements') : t('add element') }}
                    content={inPairs ? t('add pair of elements') : t('add element')}
                />
            </div>
        </div>
    );
}
export default EditAnswers;